import React, { useState, useEffect } from 'react';
import { Clock, MapPin, Users, ChevronLeft, ChevronRight, Plus, Cloud, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const RealEstateManagingPartnerCalendarDaily = () => {
  const navigate = useNavigate();
  // Initialize to current week (starting from the Sunday of the current week)
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - dayOfWeek); // Go back to Sunday
    return startOfWeek;
  });
  const [activeCalendar, setActiveCalendar] = useState('business');
  const [activeCategory, setActiveCategory] = useState('all');
  
  // Outlook Desktop Sync State
  const [outlookEvents, setOutlookEvents] = useState([]);
  const [outlookSyncStatus, setOutlookSyncStatus] = useState({ synced: false, last_sync: null, event_count: 0 });
  const [syncingOutlook, setSyncingOutlook] = useState(false);
  
  // Get API URL - use window.location for production
  const getApiUrl = () => {
    if (typeof window !== 'undefined') {
      // In production, use the current host
      return `${window.location.protocol}//${window.location.host}`;
    }
    return process.env.REACT_APP_BACKEND_URL || '';
  };
  const apiUrl = getApiUrl();
  
  // Fetch Outlook desktop calendar events
  const fetchOutlookEvents = async () => {
    try {
      setSyncingOutlook(true);
      console.log('Fetching Outlook events from:', `${apiUrl}/api/outlook-calendar/desktop-events`);
      const response = await fetch(`${apiUrl}/api/outlook-calendar/desktop-events`);
      const data = await response.json();
      console.log('Outlook events response:', data);
      
      if (data.status === 'success' && data.events) {
        setOutlookEvents(data.events);
        setOutlookSyncStatus({
          synced: true,
          last_sync: data.synced_at,
          event_count: data.count
        });
      } else {
        setOutlookEvents([]);
        setOutlookSyncStatus({ synced: false, last_sync: null, event_count: 0 });
      }
    } catch (error) {
      console.error('Error fetching Outlook events:', error);
      setOutlookEvents([]);
    } finally {
      setSyncingOutlook(false);
    }
  };
  
  // Check Outlook sync status on mount
  useEffect(() => {
    const checkSyncStatus = async () => {
      try {
        console.log('Checking Outlook sync status...');
        const response = await fetch(`${apiUrl}/api/outlook-calendar/desktop-sync-status`);
        const data = await response.json();
        console.log('Sync status:', data);
        setOutlookSyncStatus(data);
        if (data.synced) {
          fetchOutlookEvents();
        }
      } catch (error) {
        console.error('Error checking Outlook sync status:', error);
      }
    };
    if (apiUrl) {
      checkSyncStatus();
    }
  }, [apiUrl]);

  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  // Sample events for the week - organized by day and category
  const weeklyEvents = {
    business: {
      all: {
        sunday: [],
        monday: [
          { hour: 6, duration: 1.5, title: 'Morning Gym', time: '6:00 AM', color: 'orange', location: 'Equinox', category: 'fitness' },
          { hour: 9, duration: 2, title: 'Team Meeting', time: '9:00 AM', color: 'blue', location: 'Conference Room A', attendees: ['Sarah', 'John'], value: 'Strategic', category: 'meetings' }
        ],
        tuesday: [
          { hour: 10, duration: 1.5, title: 'Client Presentation', time: '10:00 AM', color: 'blue', location: '450 Park Ave', value: '$2.5M', category: 'meetings' },
          { hour: 15, duration: 1, title: 'Contract Review', time: '3:00 PM', color: 'yellow', location: 'Legal Office', value: '$3.1M', category: 'deadlines' }
        ],
        wednesday: [
          { hour: 8, duration: 1, title: 'Morning Briefing', time: '8:00 AM', color: 'blue', location: 'Main Office', category: 'meetings' },
          { hour: 13, duration: 2, title: 'Client Lunch', time: '1:00 PM', color: 'blue', location: 'The Smith NYC', value: '$8M', category: 'meetings' },
          { hour: 19, duration: 2, title: 'Client Dinner', time: '7:00 PM', color: 'blue', location: 'Capital Grille', value: '$8M', category: 'meetings' }
        ],
        thursday: [
          { hour: 9, duration: 1, title: 'Site Visit', time: '9:00 AM', color: 'purple', location: 'Downtown', value: '$15M', category: 'site-visits' },
          { hour: 14, duration: 2, title: 'Financial Review', time: '2:00 PM', color: 'yellow', location: 'Conference B', value: 'Q1', category: 'deadlines' }
        ],
        friday: [
          { hour: 10, duration: 1, title: 'Deal Closing', time: '10:00 AM', color: 'green', location: 'Madison Ave', value: '$6.2M', category: 'deals' },
          { hour: 15, duration: 1, title: 'Week Wrap-up', time: '3:00 PM', color: 'blue', location: 'Main Office', category: 'meetings' }
        ],
        saturday: [
          { hour: 10, duration: 2, title: 'Property Showing', time: '10:00 AM', color: 'green', location: 'Brooklyn', value: '$4.8M', category: 'deals' }
        ]
      },
      meetings: {
        sunday: [],
        monday: [
          { hour: 9, duration: 2, title: 'Team Meeting', time: '9:00 AM', color: 'blue', location: 'Conference Room A', attendees: ['Sarah', 'John'], value: 'Strategic', category: 'meetings' }
        ],
        tuesday: [
          { hour: 10, duration: 1.5, title: 'Client Presentation', time: '10:00 AM', color: 'blue', location: '450 Park Ave', value: '$2.5M', category: 'meetings' }
        ],
        wednesday: [
          { hour: 8, duration: 1, title: 'Morning Briefing', time: '8:00 AM', color: 'blue', location: 'Main Office', category: 'meetings' },
          { hour: 13, duration: 2, title: 'Client Lunch', time: '1:00 PM', color: 'blue', location: 'The Smith NYC', value: '$8M', category: 'meetings' },
          { hour: 19, duration: 2, title: 'Client Dinner', time: '7:00 PM', color: 'blue', location: 'Capital Grille', value: '$8M', category: 'meetings' }
        ],
        thursday: [],
        friday: [
          { hour: 15, duration: 1, title: 'Week Wrap-up', time: '3:00 PM', color: 'blue', location: 'Main Office', category: 'meetings' }
        ],
        saturday: []
      },
      deals: {
        sunday: [],
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [
          { hour: 10, duration: 1, title: 'Deal Closing', time: '10:00 AM', color: 'green', location: 'Madison Ave', value: '$6.2M', category: 'deals' }
        ],
        saturday: [
          { hour: 10, duration: 2, title: 'Property Showing', time: '10:00 AM', color: 'green', location: 'Brooklyn', value: '$4.8M', category: 'deals' }
        ]
      },
      deadlines: {
        sunday: [],
        monday: [],
        tuesday: [
          { hour: 15, duration: 1, title: 'Contract Review', time: '3:00 PM', color: 'yellow', location: 'Legal Office', value: '$3.1M', category: 'deadlines' }
        ],
        wednesday: [],
        thursday: [
          { hour: 14, duration: 2, title: 'Financial Review', time: '2:00 PM', color: 'yellow', location: 'Conference B', value: 'Q1', category: 'deadlines' }
        ],
        friday: [],
        saturday: []
      },
      'site-visits': {
        sunday: [],
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [
          { hour: 9, duration: 1, title: 'Site Visit', time: '9:00 AM', color: 'purple', location: 'Downtown', value: '$15M', category: 'site-visits' }
        ],
        friday: [],
        saturday: []
      }
    },
    personal: {
      all: {
        sunday: [
          { hour: 9, duration: 2, title: 'Sunday Brunch', time: '9:00 AM', color: 'pink', location: 'Brunch Cafe', category: 'hobbies' }
        ],
        monday: [
          { hour: 6, duration: 1.5, title: 'Morning Run', time: '6:00 AM', color: 'orange', location: 'Central Park', category: 'fitness' },
          { hour: 19, duration: 1, title: 'Meditation Session', time: '7:00 PM', color: 'indigo', location: 'Home', category: 'personal' }
        ],
        tuesday: [
          { hour: 17, duration: 2, title: 'Piano Lesson', time: '5:00 PM', color: 'teal', location: 'Music Studio', category: 'hobbies' }
        ],
        wednesday: [
          { hour: 7, duration: 1, title: 'Spin Class', time: '7:00 AM', color: 'orange', location: 'SoulCycle', category: 'fitness' },
          { hour: 18, duration: 3, title: 'Family Dinner', time: '6:00 PM', color: 'pink', location: 'Home', category: 'family' }
        ],
        thursday: [
          { hour: 8, duration: 1, title: 'Yoga Class', time: '8:00 AM', color: 'orange', location: 'Yoga Studio', category: 'fitness' },
          { hour: 15, duration: 1, title: 'Doctor Checkup', time: '3:00 PM', color: 'indigo', location: 'Medical Center', category: 'personal' }
        ],
        friday: [
          { hour: 19, duration: 3, title: 'Date Night', time: '7:00 PM', color: 'pink', location: 'Restaurant', category: 'family' }
        ],
        saturday: [
          { hour: 10, duration: 2, title: 'Tennis Match', time: '10:00 AM', color: 'orange', location: 'Tennis Club', category: 'fitness' },
          { hour: 14, duration: 2, title: 'Art Gallery Visit', time: '2:00 PM', color: 'teal', location: 'MoMA', category: 'hobbies' },
          { hour: 20, duration: 2, title: 'Game Night', time: '8:00 PM', color: 'teal', location: 'Friends House', category: 'hobbies' }
        ]
      },
      fitness: {
        sunday: [],
        monday: [
          { hour: 6, duration: 1.5, title: 'Morning Run', time: '6:00 AM', color: 'orange', location: 'Central Park', category: 'fitness' }
        ],
        tuesday: [],
        wednesday: [
          { hour: 7, duration: 1, title: 'Spin Class', time: '7:00 AM', color: 'orange', location: 'SoulCycle', category: 'fitness' }
        ],
        thursday: [
          { hour: 8, duration: 1, title: 'Yoga Class', time: '8:00 AM', color: 'orange', location: 'Yoga Studio', category: 'fitness' }
        ],
        friday: [],
        saturday: [
          { hour: 10, duration: 2, title: 'Tennis Match', time: '10:00 AM', color: 'orange', location: 'Tennis Club', category: 'fitness' }
        ]
      },
      family: {
        sunday: [],
        monday: [],
        tuesday: [],
        wednesday: [
          { hour: 18, duration: 3, title: 'Family Dinner', time: '6:00 PM', color: 'pink', location: 'Home', category: 'family' }
        ],
        thursday: [],
        friday: [
          { hour: 19, duration: 3, title: 'Date Night', time: '7:00 PM', color: 'pink', location: 'Restaurant', category: 'family' }
        ],
        saturday: []
      },
      personal: {
        sunday: [],
        monday: [
          { hour: 19, duration: 1, title: 'Meditation Session', time: '7:00 PM', color: 'indigo', location: 'Home', category: 'personal' }
        ],
        tuesday: [],
        wednesday: [],
        thursday: [
          { hour: 15, duration: 1, title: 'Doctor Checkup', time: '3:00 PM', color: 'indigo', location: 'Medical Center', category: 'personal' }
        ],
        friday: [],
        saturday: []
      },
      hobbies: {
        sunday: [
          { hour: 9, duration: 2, title: 'Sunday Brunch', time: '9:00 AM', color: 'pink', location: 'Brunch Cafe', category: 'hobbies' }
        ],
        monday: [],
        tuesday: [
          { hour: 17, duration: 2, title: 'Piano Lesson', time: '5:00 PM', color: 'teal', location: 'Music Studio', category: 'hobbies' }
        ],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [
          { hour: 14, duration: 2, title: 'Art Gallery Visit', time: '2:00 PM', color: 'teal', location: 'MoMA', category: 'hobbies' },
          { hour: 20, duration: 2, title: 'Game Night', time: '8:00 PM', color: 'teal', location: 'Friends House', category: 'hobbies' }
        ]
      }
    }
  };

  const handlePrevWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeekStart(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeekStart(newDate);
  };

  const getWeekDates = () => {
    const dates = [];
    const startOfWeek = new Date(currentWeekStart);
    const dayOfWeek = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek); // Start from Sunday
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const formatWeekRange = () => {
    const weekDates = getWeekDates();
    const startDate = weekDates[0];
    const endDate = weekDates[6];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[startDate.getMonth()]} ${startDate.getDate()} - ${months[endDate.getMonth()]} ${endDate.getDate()}, ${startDate.getFullYear()}`;
  };

  const getDayKey = (index) => {
    const keys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return keys[index];
  };

  const getEventsForDay = (dayIndex) => {
    const dayKey = getDayKey(dayIndex);
    const weekDates = getWeekDates();
    const currentDate = weekDates[dayIndex];
    const dateStr = currentDate.toISOString().split('T')[0]; // YYYY-MM-DD format
    
    // Only show Outlook events (no hardcoded events)
    if (!outlookSyncStatus.synced) {
      return [];
    }
    
    // Filter Outlook events for this day and category
    const dayOutlookEvents = outlookEvents
      .filter(event => {
        // Filter by date
        if (event.date !== dateStr) return false;
        
        // Filter by category if not 'all'
        if (activeCategory !== 'all') {
          const eventCat = event.category || event.type || 'other';
          if (eventCat !== activeCategory) return false;
        }
        
        return true;
      })
      .map(event => {
        // Parse the start time to get hour
        let hour = 9; // default
        if (event.time) {
          const timeMatch = event.time.match(/(\d+):(\d+)\s*(AM|PM)?/i);
          if (timeMatch) {
            hour = parseInt(timeMatch[1]);
            if (timeMatch[3] && timeMatch[3].toUpperCase() === 'PM' && hour !== 12) {
              hour += 12;
            }
            if (timeMatch[3] && timeMatch[3].toUpperCase() === 'AM' && hour === 12) {
              hour = 0;
            }
          }
        }
        
        // Get the color based on category
        const eventCategory = event.category || event.type || 'other';
        const color = getCategoryColor(eventCategory);
        
        return {
          hour: hour,
          duration: 1,
          title: event.subject || event.title,
          time: event.time || '',
          color: color,
          location: event.location || '',
          category: eventCategory,
          source: 'outlook',
          // Add unique identifier for deduplication
          eventId: event.id || `${event.subject || event.title}-${event.time || ''}-${event.date}`
        };
      });
    
    // Deduplicate events by title and time to prevent duplicates
    const uniqueEvents = dayOutlookEvents.reduce((acc, event) => {
      const eventKey = `${event.title}-${event.time}`.toLowerCase();
      if (!acc.some(e => `${e.title}-${e.time}`.toLowerCase() === eventKey)) {
        acc.push(event);
      }
      return acc;
    }, []);
    
    // Sort by hour
    return uniqueEvents.sort((a, b) => a.hour - b.hour);
  };

  const weekDates = getWeekDates();
  
  // Calculate total events from Outlook for this week
  const totalEvents = outlookEvents.filter(event => {
    const eventDate = new Date(event.date);
    const weekStart = weekDates[0];
    const weekEnd = weekDates[6];
    return eventDate >= weekStart && eventDate <= weekEnd;
  }).length;

  // Outlook category definitions with colors
  const outlookCategories = [
    { id: 'all', label: 'All', color: 'gray' },
    { id: 'meeting', label: 'Meetings', color: 'blue' },
    { id: 'work', label: 'Work', color: 'blue' },
    { id: 'cresa', label: 'Cresa', color: 'emerald' },
    { id: 'birthday', label: 'Birthdays', color: 'pink' },
    { id: 'health', label: 'Health', color: 'red' },
    { id: 'sports', label: 'Sports', color: 'orange' },
    { id: 'religious', label: 'Religious', color: 'purple' },
    { id: 'travel', label: 'Travel', color: 'cyan' },
    { id: 'home', label: 'Home', color: 'green' },
    { id: 'social', label: 'Social', color: 'yellow' },
    { id: 'education', label: 'Education', color: 'indigo' },
    { id: 'networking', label: 'Networking', color: 'teal' },
    { id: 'other', label: 'Other', color: 'gray' }
  ];
  
  // Get category counts from Outlook events
  const getCategoryCounts = () => {
    const counts = {};
    outlookEvents.forEach(event => {
      const cat = event.category || event.type || 'other';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  };
  const categoryCounts = getCategoryCounts();
  
  // Filter categories that have events
  const availableOutlookCategories = outlookCategories.filter(
    cat => cat.id === 'all' || categoryCounts[cat.id] > 0
  );

  const getCategoryColor = (category) => {
    const colors = {
      'all': 'gray',
      'meeting': 'blue',
      'work': 'blue',
      'cresa': 'emerald',
      'birthday': 'pink',
      'health': 'red',
      'sports': 'orange',
      'religious': 'purple',
      'travel': 'cyan',
      'home': 'green',
      'social': 'yellow',
      'education': 'indigo',
      'networking': 'teal',
      'other': 'gray'
    };
    return colors[category] || 'gray';
  };
  
  const getColorClasses = (color) => {
    const colorMap = {
      'blue': 'bg-blue-600 border-blue-400',
      'red': 'bg-red-600 border-red-400',
      'orange': 'bg-orange-500 border-orange-400',
      'pink': 'bg-pink-500 border-pink-400',
      'purple': 'bg-purple-600 border-purple-400',
      'cyan': 'bg-cyan-500 border-cyan-400',
      'green': 'bg-green-600 border-green-400',
      'yellow': 'bg-yellow-500 border-yellow-400 text-black',
      'indigo': 'bg-indigo-600 border-indigo-400',
      'teal': 'bg-teal-500 border-teal-400',
      'emerald': 'bg-emerald-500 border-emerald-400',
      'gray': 'bg-gray-600 border-gray-400'
    };
    return colorMap[color] || 'bg-gray-600 border-gray-400';
  };

  const getCategoryLabel = (category) => {
    const cat = outlookCategories.find(c => c.id === category);
    return cat ? cat.label : category;
  };

  const handleCalendarSwitch = (type) => {
    setActiveCalendar(type);
    setActiveCategory('all');
  };

  return (
    <div className="min-h-screen bg-black text-white p-6">
      {/* Unified Header Container */}
      <div className="mb-6 bg-black border border-gray-400 rounded-xl p-4">
        {/* Row 1: Title, View Toggles, Navigation */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold mokoto-font">{formatWeekRange()}</h1>

          <div className="flex items-center gap-4">
            {/* Outlook Sync Button */}
            <button
              onClick={fetchOutlookEvents}
              disabled={syncingOutlook}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                outlookSyncStatus.synced 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                  : 'bg-gray-700 hover:bg-gray-600 text-white'
              }`}
            >
              {syncingOutlook ? (
                <RefreshCw size={14} className="animate-spin" />
              ) : (
                <Cloud size={14} />
              )}
              {outlookSyncStatus.synced ? `Outlook (${outlookSyncStatus.event_count})` : 'Load Outlook'}
            </button>
            
            {/* View Toggle Buttons */}
            <div className="flex gap-1">
              <button
                onClick={() => navigate('/calendar-re')}
                className="px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors bg-gray-800 text-white hover:bg-gray-700"
              >
                Month
              </button>
              <button
                className="px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors bg-white text-black"
              >
                Week
              </button>
            </div>

            {/* Navigation Arrows */}
            <div className="flex items-center gap-1">
              <button 
                onClick={handlePrevWeek}
                className="p-1.5 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
              <button 
                onClick={() => {
                  const today = new Date();
                  const dayOfWeek = today.getDay();
                  const startOfWeek = new Date(today);
                  startOfWeek.setDate(today.getDate() - dayOfWeek);
                  setCurrentWeekStart(startOfWeek);
                }}
                className="px-3 py-1.5 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors text-sm"
              >
                Today
              </button>
              <button 
                onClick={handleNextWeek}
                className="p-1.5 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Row 2: Sync Status */}
        {outlookSyncStatus.synced && (
          <div className="mb-3 text-sm text-gray-400">
            <Cloud size={14} className="inline mr-2" />
            Last sync: {outlookSyncStatus.last_sync ? new Date(outlookSyncStatus.last_sync).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) + ' CT' : 'N/A'}
          </div>
        )}

        {/* Row 3: Category Filter Tabs */}
        {outlookSyncStatus.synced && (
          <div className="flex gap-2 flex-wrap">
            {availableOutlookCategories.map((cat) => {
              const count = cat.id === 'all' ? outlookEvents.length : (categoryCounts[cat.id] || 0);
              const isActive = activeCategory === cat.id;
              
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                    isActive
                      ? `${getColorClasses(cat.color)} text-white border-2`
                      : 'bg-gray-800 border border-gray-600 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${getColorClasses(cat.color).split(' ')[0]}`}></span>
                  {cat.label}
                  <span className={`text-xs ${isActive ? 'text-white/80' : 'text-gray-500'}`}>({count})</span>
                </button>
              );
            })}
          </div>
        )}
        
        {/* No Outlook Connected Message */}
        {!outlookSyncStatus.synced && (
          <div className="flex items-center gap-3 text-sm text-gray-400">
            <Cloud size={16} className="text-gray-500" />
            <span>No Outlook calendar connected • Run the desktop sync script to import your events</span>
          </div>
        )}
      </div>

      {/* Week Day Headers */}
      <div className="grid grid-cols-7 gap-3 mb-6">
        {weekDates.map((date, index) => (
          <div
            key={index}
            className="bg-black border border-gray-400 rounded-xl p-4 text-center"
          >
            <div className="text-xs text-gray-400 mb-1">{daysOfWeek[date.getDay()]}</div>
            <div className="text-2xl font-bold">{date.getDate()}</div>
          </div>
        ))}
      </div>

      {/* Daily Schedule Grid - Vertical Cards for Each Day */}
      <div className="grid grid-cols-7 gap-3">
        {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => {
          const dayEvents = getEventsForDay(dayIndex);
          
          const getEventCardClasses = (color) => {
            const colorStyles = {
              'blue': 'border-blue-500 bg-blue-500/10',
              'red': 'border-red-500 bg-red-500/10',
              'orange': 'border-orange-500 bg-orange-500/10',
              'pink': 'border-pink-500 bg-pink-500/10',
              'purple': 'border-purple-500 bg-purple-500/10',
              'cyan': 'border-cyan-500 bg-cyan-500/10',
              'green': 'border-green-500 bg-green-500/10',
              'yellow': 'border-yellow-500 bg-yellow-500/10',
              'indigo': 'border-indigo-500 bg-indigo-500/10',
              'teal': 'border-teal-500 bg-teal-500/10',
              'gray': 'border-gray-500 bg-gray-500/10'
            };
            return colorStyles[color] || 'border-gray-500 bg-gray-500/10';
          };
          
          return (
            <div key={dayIndex} className="bg-black border border-gray-400 rounded-xl p-3 min-h-[600px]">
              {/* Time Labels */}
              <div className="space-y-2">
                {/* Morning */}
                <div className="text-xs text-gray-500 mb-2">6 AM</div>
                {dayEvents
                  .filter(e => e.hour >= 6 && e.hour < 12)
                  .map((event, idx) => (
                    <div
                      key={idx}
                      className={`rounded-xl border-2 p-3 mb-2 overflow-hidden ${getEventCardClasses(event.color)}`}
                      style={{ minHeight: `${event.duration * 40}px` }}
                    >
                      <div className="font-bold text-white text-sm truncate mb-1">{event.title}</div>
                      <div className="flex items-center gap-1 text-xs text-gray-300 mb-1">
                        <Clock size={10} />
                        <span className="truncate">{event.time}</span>
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-1 text-xs text-gray-400 truncate">
                          <MapPin size={10} />
                          <span className="truncate">{event.location}</span>
                        </div>
                      )}
                      {event.value && (
                        <div className="mt-1 px-2 py-0.5 bg-white/20 rounded text-xs font-semibold inline-block">
                          {event.value}
                        </div>
                      )}
                    </div>
                  ))}
                
                {/* Afternoon */}
                <div className="text-xs text-gray-500 mb-2 mt-4">12 PM</div>
                {dayEvents
                  .filter(e => e.hour >= 12 && e.hour < 18)
                  .map((event, idx) => (
                    <div
                      key={idx}
                      className={`rounded-xl border-2 p-3 mb-2 overflow-hidden ${getEventCardClasses(event.color)}`}
                      style={{ minHeight: `${event.duration * 40}px` }}
                    >
                      <div className="font-bold text-white text-sm truncate mb-1">{event.title}</div>
                      <div className="flex items-center gap-1 text-xs text-gray-300 mb-1">
                        <Clock size={10} />
                        <span className="truncate">{event.time}</span>
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-1 text-xs text-gray-400 truncate">
                          <MapPin size={10} />
                          <span className="truncate">{event.location}</span>
                        </div>
                      )}
                      {event.value && (
                        <div className="mt-1 px-2 py-0.5 bg-white/20 rounded text-xs font-semibold inline-block">
                          {event.value}
                        </div>
                      )}
                    </div>
                  ))}
                
                {/* Evening */}
                <div className="text-xs text-gray-500 mb-2 mt-4">6 PM</div>
                {dayEvents
                  .filter(e => e.hour >= 18)
                  .map((event, idx) => (
                    <div
                      key={idx}
                      className={`rounded-xl border-2 p-3 mb-2 overflow-hidden ${getEventCardClasses(event.color)}`}
                      style={{ minHeight: `${event.duration * 40}px` }}
                    >
                      <div className="font-bold text-white text-sm truncate mb-1">{event.title}</div>
                      <div className="flex items-center gap-1 text-xs text-gray-300 mb-1">
                        <Clock size={10} />
                        <span className="truncate">{event.time}</span>
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-1 text-xs text-gray-400 truncate">
                          <MapPin size={10} />
                          <span className="truncate">{event.location}</span>
                        </div>
                      )}
                      {event.value && (
                        <div className="mt-1 px-2 py-0.5 bg-white/20 rounded text-xs font-semibold inline-block">
                          {event.value}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
              
              {/* Add Event Button at bottom of empty space */}
              <div className="mt-4">
                <button className="w-full p-2 border border-dashed border-gray-600 rounded-lg hover:border-white transition-colors flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-white">
                  <Plus size={16} />
                  <span>Add Event</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RealEstateManagingPartnerCalendarDaily;
